import { ReactElement, useMemo } from 'react';
import { t } from 'i18next';

import { MaintenanceViewBy } from '../../types';
import { MaintenanceAnalytics } from '../../helpers/api';
import { DonutCard } from './parts/DonutCard';
import { DonutItem } from './parts/DonutCard/types';
import { Container } from './styles';
import { useDonutTier } from './tier';

interface DonutsProps {
  analytics: MaintenanceAnalytics | undefined;
  loading: boolean;
  viewBy: MaintenanceViewBy;
}

type Bucket = MaintenanceAnalytics['BRANDS'];

function toItems(
  bucket: Bucket | undefined,
  viewBy: MaintenanceViewBy,
): DonutItem[] {
  if (!bucket) return [];
  return [...bucket]
    .map((row) => {
      const value = viewBy === 'capacity' ? row.COOLING_CAPACITY_TR : row.MACHINES_COUNT;
      const percentage = viewBy === 'capacity' ? row.COOLING_CAPACITY_PERCENTAGE : row.MACHINES_PERCENTAGE;
      return { name: row.NAME, value, percentage };
    })
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
}

export function Donuts({ analytics, loading, viewBy }: Readonly<DonutsProps>): ReactElement {
  const tier = useDonutTier();

  const brandsItems = useMemo(() => toItems(analytics?.BRANDS, viewBy), [analytics, viewBy]);
  const typesItems = useMemo(() => toItems(analytics?.TYPES, viewBy), [analytics, viewBy]);
  const fluidsItems = useMemo(() => toItems(analytics?.FLUIDS, viewBy), [analytics, viewBy]);

  const isCapacity = viewBy === 'capacity';
  const brandsTitle = isCapacity ? t('trPorFabricante') : t('maquinasPorFabricante');
  const typesTitle = isCapacity ? `${t('tiposDeMaquinas')} (${t('emTr')})` : t('tiposDeMaquinas');
  const fluidsTitle = isCapacity ? `${t('fluidosRefrigerantes')} (${t('emTr')})` : t('fluidosRefrigerantes');

  return (
    <Container>
      <DonutCard
        title={brandsTitle}
        unit={viewBy}
        items={loading ? undefined : brandsItems}
        loading={loading}
        tier={tier}
      />
      <DonutCard
        title={typesTitle}
        unit={viewBy}
        items={loading ? undefined : typesItems}
        loading={loading}
        tier={tier}
      />
      <DonutCard
        title={fluidsTitle}
        unit={viewBy}
        items={loading ? undefined : fluidsItems}
        loading={loading}
        tier={tier}
      />
    </Container>
  );
}
